import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { WebinarRunDTO } from 'src/app/Services/WOWApi.Service';
import { DynamicDialogConfig } from '../congratulations-popup/congratulations-popup.component';

type PointerTarget = 'min' | 'max' | 'button' | 'result' | null;

interface RoundCopy {
  kicker: string;
  title: string;
  note: string;
  /** Official rounds decide the prize; calibration rounds are a visible self-test. */
  official: boolean;
  /** 1..3 for calibration, 0 for the official draw. */
  step: number;
}

const ROUND_COPY: { [status: string]: RoundCopy } = {
  'First Dummy Run': {
    kicker: 'System check',
    title: 'System Check 1 of 3',
    note: 'Test sequence — no seats are in play',
    official: false,
    step: 1
  },
  'Second Dummy Run': {
    kicker: 'System check',
    title: 'System Check 2 of 3',
    note: 'Test sequence — no seats are in play',
    official: false,
    step: 2
  },
  'Third Dummy Run': {
    kicker: 'System check',
    title: 'System Check 3 of 3',
    note: 'Test sequence — no seats are in play',
    official: false,
    step: 3
  },
  'Final Run': {
    kicker: 'Official draw',
    title: 'Live Drawing',
    note: 'Drawn from the seats in play — this is the one that counts',
    official: true,
    step: 0
  },
  'Completed': {
    kicker: 'Result certified',
    title: 'Winner Confirmed',
    note: '',
    official: true,
    step: 0
  }
};

@Component({
    selector: 'app-display-results',
    templateUrl: './display-results.component.html',
    styleUrls: ['./display-results.component.css'],
    standalone: false
})
export class DisplayResultsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() webinarRun: WebinarRunDTO;
  @Input() status: string;
  @Output() congrtsPopup = new EventEmitter<DynamicDialogConfig>();

  @ViewChild('panel') panel: ElementRef<HTMLElement>;
  @ViewChild('minCard') minCard: ElementRef<HTMLElement>;
  @ViewChild('maxCard') maxCard: ElementRef<HTMLElement>;
  @ViewChild('generateButton') generateButton: ElementRef<HTMLElement>;
  @ViewChild('resultCard') resultCard: ElementRef<HTMLElement>;

  copy: RoundCopy | null = null;
  min: number | null = null;
  max: number | null = null;
  result = '';
  displayUserName: string = null;
  buttonClick = false;
  resultRolling = false;
  resultLanded = false;
  roundComplete = false;
  pointerAt: PointerTarget = null;
  handPosition = { left: 0, top: 0 };
  /** Hand stays hidden until a real anchor has been measured, so it never flashes at 0,0. */
  pointerVisible = false;
  /** False while the anchor sits outside the viewport, so no stray hand is left behind. */
  handOnScreen = true;
  /** Suppresses the glide transition while re-anchoring during a scroll. */
  handTracking = false;

  /** Matches the transition duration on .draw-hand so steps wait for the glide to land. */
  private static readonly TRAVEL_MS = 900;
  private static readonly SETTLE_MS = 300;
  private static readonly DIGIT_MS = 170;
  private static readonly PRESS_MS = 240;
  private static readonly ROLL_STEPS = 14;

  /** Bumped on every sequence start; in-flight steps from an older run abort on sight. */
  private runId = 0;
  private lastStatus: string | null = null;
  private popupEmitted = false;
  private destroyed = false;
  private rollBounds: { min: number; max: number } | null = null;
  private trackFrame = 0;
  private timers = new Set<ReturnType<typeof setTimeout>>();

  get isOfficial(): boolean {
    return !!this.copy?.official;
  }

  get calibrationStep(): number {
    return this.copy && !this.copy.official ? this.copy.step : 0;
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.webinarRun || !this.status) {
      return;
    }
    // Every SignalR push replaces the DTO, which re-fires ngOnChanges. Restarting the
    // sequence on an unchanged status is what made the animation stutter and jump.
    if (this.status === this.lastStatus) {
      return;
    }
    this.lastStatus = this.status;
    void this.runSequence();
  }

  ngOnInit(): void {
    // The hand is position:fixed against viewport coordinates, so any scroll invalidates
    // its anchor. This matters most on mobile, where the run page auto-scrolls to the
    // results panel exactly as the first round starts.
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    window.addEventListener('orientationchange', this.onViewportChange, { passive: true });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.runId++;
    this.clearTimers();
    window.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('orientationchange', this.onViewportChange);
    if (this.trackFrame) {
      cancelAnimationFrame(this.trackFrame);
      this.trackFrame = 0;
    }
  }

  private onViewportChange = (): void => {
    if (!this.pointerVisible || !this.pointerAt || this.trackFrame) {
      return;
    }
    this.trackFrame = requestAnimationFrame(() => {
      this.trackFrame = 0;
      if (this.destroyed || !this.pointerAt) {
        return;
      }
      this.handTracking = true;
      this.positionHand(this.pointerAt);
    });
  };

  private async runSequence(): Promise<void> {
    const id = ++this.runId;
    this.clearTimers();

    const copy = ROUND_COPY[this.status];
    if (!copy) {
      this.copy = null;
      return;
    }

    this.copy = copy;
    this.min = null;
    this.max = null;
    this.result = '';
    this.displayUserName = null;
    this.buttonClick = false;
    this.resultRolling = false;
    this.resultLanded = false;
    this.roundComplete = false;
    this.pointerAt = null;
    this.pointerVisible = false;

    const range = copy.official ? this.officialRange() : this.calibrationRange();
    if (!range) {
      return;
    }
    this.rollBounds = range;

    // Let the template paint the panel so the anchors can be measured.
    await this.wait(60);
    if (!this.alive(id)) { return; }

    await this.ensurePanelVisible(id);
    if (!this.alive(id)) { return; }

    await this.moveTo('min', id);
    if (!this.alive(id)) { return; }
    this.min = range.min;
    await this.wait(DisplayResultsComponent.SETTLE_MS);
    if (!this.alive(id)) { return; }

    await this.moveTo('max', id);
    if (!this.alive(id)) { return; }
    await this.typeNumber(range.max, id);
    if (!this.alive(id)) { return; }
    await this.wait(DisplayResultsComponent.SETTLE_MS);
    if (!this.alive(id)) { return; }

    await this.moveTo('button', id);
    if (!this.alive(id)) { return; }
    await this.wait(this.jitter(260, 120));
    if (!this.alive(id)) { return; }

    this.buttonClick = true;
    await this.wait(DisplayResultsComponent.PRESS_MS);
    if (!this.alive(id)) { return; }
    this.buttonClick = false;

    await this.moveTo('result', id);
    if (!this.alive(id)) { return; }
    await this.rollTo(range.drawn.toString(), id);
    if (!this.alive(id)) { return; }

    this.resultLanded = true;
    await this.wait(600);
    if (!this.alive(id)) { return; }

    this.pointerVisible = false;
    this.roundComplete = true;

    if (copy.official) {
      await this.revealWinner(id);
    }
  }

  /**
   * Calibration rounds draw from the machine's full range. The number is deliberately
   * not tied to a seat - the grid highlight is suppressed for these rounds so nobody
   * can mistake a test number for a win.
   */
  private calibrationRange(): { min: number; max: number; drawn: number } | null {
    const min = this.toPositive(this.rangeMin()) || 1;
    const max = this.toPositive(this.rangeMax());
    if (!max || max <= min) {
      return null;
    }
    const drawn = this.toPositive(this.webinarRun.selectedSeat) || this.randomAboveSeats(min, max);
    return { min, max, drawn };
  }

  /** The official draw is bounded by the real seats in play. */
  private officialRange(): { min: number; max: number; drawn: number } | null {
    const seatNumbers = (this.webinarRun.userSeats || [])
      .map(s => this.toPositive(s.seatNumber))
      .filter(n => n > 0);
    if (!seatNumbers.length) {
      return null;
    }
    const winner = this.winnerSeat();
    return {
      min: Math.min(...seatNumbers),
      max: Math.max(...seatNumbers),
      drawn: winner ? winner.seatNumber : Math.max(...seatNumbers)
    };
  }

  private winnerSeat() {
    const selected = this.toPositive(this.webinarRun.selectedSeat);
    if (!selected) {
      return null;
    }
    const seats = this.webinarRun.userSeats || [];
    // The API sends the winner as a seat *id*; fall back to seat number for the demo
    // harness and for older payloads.
    return seats.find(s => s.seatId === selected) || seats.find(s => s.seatNumber === selected) || null;
  }

  private async revealWinner(id: number): Promise<void> {
    const winner = this.winnerSeat();
    if (!winner || this.popupEmitted) {
      return;
    }
    this.displayUserName = (winner.userName || '').trim() || 'Unknown User';
    await this.wait(1400);
    if (!this.alive(id) || this.popupEmitted) {
      return;
    }
    this.popupEmitted = true;

    const info = new DynamicDialogConfig();
    info.UserName = this.displayUserName;
    info.SeatNumber = winner.seatNumber;
    info.ItemImageUrl = this.webinarRun.itemImageUrl;
    info.WebinarName = this.webinarRun.webinarName;
    this.congrtsPopup.emit(info);
  }

  private rangeMin(): number {
    switch (this.status) {
      case 'First Dummy Run': return this.webinarRun.firstDummyRunMin;
      case 'Second Dummy Run': return this.webinarRun.secondDummyRunMin;
      case 'Third Dummy Run': return this.webinarRun.thirdDummyRunMin;
      default: return 0;
    }
  }

  private rangeMax(): number {
    switch (this.status) {
      case 'First Dummy Run': return this.webinarRun.firstDummyRunMax;
      case 'Second Dummy Run': return this.webinarRun.secondDummyRunMax;
      case 'Third Dummy Run': return this.webinarRun.thirdDummyRunMax;
      default: return 0;
    }
  }

  /** Keeps a fabricated fallback number clear of every seat number on screen. */
  private randomAboveSeats(min: number, max: number): number {
    const seatCeiling = (this.webinarRun.userSeats || [])
      .reduce((hi, s) => Math.max(hi, this.toPositive(s.seatNumber)), 0);
    const floor = Math.max(min, seatCeiling + 1);
    if (floor >= max) {
      return max;
    }
    return floor + Math.floor(Math.random() * (max - floor));
  }

  /** Types the max digit by digit, the way a person would key it in. */
  private async typeNumber(value: number, id: number): Promise<void> {
    const digits = value.toString().split('');
    let typed = '';
    for (const digit of digits) {
      typed += digit;
      this.max = Number(typed);
      await this.wait(this.jitter(DisplayResultsComponent.DIGIT_MS, 70));
      if (!this.alive(id)) {
        return;
      }
    }
  }

  /** Spins through same-width numbers and decelerates onto the real one. */
  private async rollTo(finalValue: string, id: number): Promise<void> {
    this.resultRolling = true;
    const width = finalValue.length;
    for (let i = 0; i < DisplayResultsComponent.ROLL_STEPS; i++) {
      this.result = this.randomRollValue(width);
      await this.wait(45 + i * 14);
      if (!this.alive(id)) {
        this.resultRolling = false;
        return;
      }
    }
    this.resultRolling = false;
    this.result = finalValue;
  }

  /**
   * A spin value that keeps the digit count steady (so the box does not jump about) while
   * staying inside the min/max on screen - spinning past the stated max looks broken.
   */
  private randomRollValue(width: number): string {
    const bounds = this.rollBounds;
    const low = Math.max(bounds ? bounds.min : 1, Math.pow(10, width - 1));
    const high = bounds ? bounds.max : Math.pow(10, width) - 1;
    if (low >= high) {
      return (bounds ? bounds.max : high).toString();
    }
    return (low + Math.floor(Math.random() * (high - low + 1))).toString();
  }

  /**
   * Points the hand at an anchor and waits out the CSS glide, so movement reads as
   * travel rather than teleporting between elements.
   */
  private async moveTo(target: Exclude<PointerTarget, null>, id: number): Promise<void> {
    if (!this.anchorFor(target)) {
      return;
    }
    const wasVisible = this.pointerVisible;

    this.pointerAt = target;
    this.handTracking = false;
    this.pointerVisible = true;
    this.positionHand(target);

    // First placement has nothing to glide from, so it only needs a beat to fade in.
    await this.wait(wasVisible ? DisplayResultsComponent.TRAVEL_MS : 320);
    if (!this.alive(id)) {
      return;
    }
  }

  /**
   * On phones this panel stacks below a tall seat grid, so the round would otherwise play
   * out entirely off-screen. Scrolls it into view only when it is not already mostly
   * visible, which makes this a no-op in the desktop side column.
   */
  private async ensurePanelVisible(id: number): Promise<void> {
    const el = this.panel?.nativeElement;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const visibleH = Math.min(rect.bottom, viewportH) - Math.max(rect.top, 0);
    if (visibleH >= Math.min(rect.height, viewportH * 0.6)) {
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    // Let the smooth scroll settle before anchors get measured for the hand.
    await this.wait(800);
    if (!this.alive(id)) {
      return;
    }
  }

  /** Measures the anchor in viewport space, which is what the fixed-position hand uses. */
  private positionHand(target: Exclude<PointerTarget, null>): void {
    const anchor = this.anchorFor(target);
    if (!anchor) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    this.handPosition = {
      left: rect.left + rect.width * (target === 'button' ? 0.42 : 0.3),
      top: rect.top + rect.height * 0.55
    };
    this.handOnScreen = rect.bottom > 0 && rect.top < window.innerHeight;
  }

  private anchorFor(target: Exclude<PointerTarget, null>): HTMLElement | null {
    switch (target) {
      case 'min': return this.minCard?.nativeElement || null;
      case 'max': return this.maxCard?.nativeElement || null;
      case 'button': return this.generateButton?.nativeElement || this.resultCard?.nativeElement || null;
      case 'result': return this.resultCard?.nativeElement || null;
      default: return null;
    }
  }

  private toPositive(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  private jitter(base: number, spread: number): number {
    return Math.max(40, base + Math.round((Math.random() - 0.5) * spread * 2));
  }

  private alive(id: number): boolean {
    return !this.destroyed && id === this.runId;
  }

  private wait(ms: number): Promise<void> {
    return new Promise<void>(resolve => {
      const handle = setTimeout(() => {
        this.timers.delete(handle);
        resolve();
      }, ms);
      this.timers.add(handle);
    });
  }

  private clearTimers(): void {
    this.timers.forEach(handle => clearTimeout(handle));
    this.timers.clear();
  }
}
